import React, { useMemo, useState } from 'react'
import { WandSparkles } from 'lucide-react'
import { Banner } from '@astryxdesign/core/Banner'
import { Card } from '@astryxdesign/core/Card'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Step, Stepper } from '@astryxdesign/core/Stepper'
import { Text } from '@astryxdesign/core/Text'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Token } from '@astryxdesign/core/Token'
import type { GenreItem } from '../lib/genres'
import { sortCoverStyles } from '../lib/coverStyles'
import { useIsMobile } from '../lib/useMediaQuery'
import BookCover from './BookCover'
import CoverStyleGallery from './CoverStyleGallery'
import PillButton from './PillButton'
import PublishReadersField from './PublishReadersField'
import StoryBlurbField, { MIN_STORY_BLURB_LENGTH } from './StoryBlurbField'

export interface PublishStoryDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title: string
  genre: string | null | undefined
  genreItems: GenreItem[]
  authorName: string
  publication: PublicationStatus | null
  blurb: string
  onBlurbChange: (value: string) => void
  onGenerateBlurb: () => void
  isGeneratingBlurb: boolean
  onPublish: () => void
  isPublishing: boolean
  visibility: PublicationVisibility
  onVisibilityChange: (value: PublicationVisibility) => void
  readers: string[]
  onReadersChange: (readers: string[]) => void
  coverEnabled: boolean
  coverUrl: string | null
  isGeneratingCover: boolean
  coverError: string | null
  onGenerateCover: () => void
  onRemoveCover: () => void
  onClearCoverError?: () => void
  coverStyles: CoverStyle[]
  coverStylesError: string | null
  selectedStyle: string | null
  onSelectStyle: (key: string | null) => void
  /** Picks a style and drafts the art direction for it; `isSuggestingPrompt` covers the draft. */
  onSuggestPrompt: (key: string) => void
  isSuggestingPrompt: boolean
  coverNote: string
  onCoverNoteChange: (v: string) => void
}

type Page = 'details' | 'cover' | 'audience'

const PAGE_LABELS: Record<Page, string> = {
  details: 'Details',
  cover: 'Cover',
  audience: 'Audience'
}

/**
 * A details page (title, genres, blurb), then with the "story-image" flag on a cover page (style
 * gallery and art-direction note above the BookCover frame, then Generate/Regenerate/Remove below
 * it), and always a final audience page holding "Who can read this" and, for restricted stories,
 * the readers list. Publish lives on that last page only and never requires a cover. The body is
 * keyed on `isOpen` so `page` resets to "details" every time the dialog is reopened, without an
 * effect.
 *
 * No Astryx `Layout` in here: nested in a `Dialog` its negative inline margins never get absorbed
 * and the content lands off-centre, which also let focusing the footer's Publish button scroll the
 * whole dialog sideways. `Dialog` is already a flex column, so a scrollable section plus a footer
 * is all this needs. See .claude/skills/astryx-dialog-layout-padding, Trap 1.
 */
export default function PublishStoryDialog(props: PublishStoryDialogProps): React.JSX.Element {
  const { isOpen, onOpenChange } = props
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      width="max(60vw, 440px)"
      maxHeight="calc(100vh - 64px)"
      purpose="form"
      className="publish-story-dialog"
    >
      <PublishStoryDialogBody key={String(isOpen)} {...props} />
    </Dialog>
  )
}

function PublishStoryDialogBody({
  onOpenChange,
  title,
  genre,
  genreItems,
  authorName,
  publication,
  blurb,
  onBlurbChange,
  onGenerateBlurb,
  isGeneratingBlurb,
  onPublish,
  isPublishing,
  visibility,
  onVisibilityChange,
  readers,
  onReadersChange,
  coverEnabled,
  coverUrl,
  isGeneratingCover,
  coverError,
  onGenerateCover,
  onRemoveCover,
  onClearCoverError,
  coverStyles,
  coverStylesError,
  selectedStyle,
  onSelectStyle,
  onSuggestPrompt,
  isSuggestingPrompt,
  coverNote,
  onCoverNoteChange
}: PublishStoryDialogProps): React.JSX.Element {
  const pages = useMemo<Page[]>(
    () => (coverEnabled ? ['details', 'cover', 'audience'] : ['details', 'audience']),
    [coverEnabled]
  )
  const [page, setPage] = useState<Page>('details')
  // The stepper is a rail down the left of the page on desktop; a phone has no width to
  // spare for one, so there it runs across the top and collapses to the current step.
  const isMobile = useIsMobile()
  // The cover page runs in three phases. "style": only the gallery, until a style is picked or
  // the author opts to write the prompt themselves. "prompt": the header names the style and
  // the art-direction note sits below it, prefilled with the drafted prompt. "cover": once an
  // image exists (or is being made) only it and Regenerate/Remove show. The first two are
  // tracked here; the third is derived from the cover itself.
  const [isWritingPrompt, setIsWritingPrompt] = useState(false)
  const selectedStyleLabel = coverStyles.find((style) => style.key === selectedStyle)?.label ?? null
  const coverPhase: 'style' | 'prompt' | 'cover' =
    coverUrl || isGeneratingCover
      ? 'cover'
      : isWritingPrompt || isSuggestingPrompt || coverNote.trim().length > 0
        ? 'prompt'
        : 'style'
  const pageIndex = Math.max(0, pages.indexOf(page))
  const isLastPage = pageIndex === pages.length - 1
  // Back and Next hold still while a cover is generating, so the stepper's own jumps do too.
  // ...and while a prompt is being drafted, since the note is about to change under the author.
  const isNavLocked =
    isPublishing || (page === 'cover' && (isGeneratingCover || isSuggestingPrompt))
  const goToStep = (index: number): void => {
    const target = pages[index]
    if (!target || target === page || isNavLocked) return
    if (page === 'cover') onClearCoverError?.()
    setPage(target)
  }
  const goBack = (): void => goToStep(pageIndex - 1)
  const goNext = (): void => goToStep(pageIndex + 1)
  const { sorted: sortedCoverStyles, suitedKeys } = useMemo(
    () => sortCoverStyles(coverStyles, genre),
    [coverStyles, genre]
  )
  const canGenerateCover = Boolean(selectedStyle) || coverNote.trim().length > 0

  // `title` is the story's raw name, so an untitled story really does block Publish. Display
  // falls back to "Untitled story"; the gate must not.
  const displayTitle = title.trim() || 'Untitled story'
  const detailsComplete =
    Boolean(title.trim()) &&
    Boolean((genre || '').trim()) &&
    blurb.trim().length >= MIN_STORY_BLURB_LENGTH &&
    !isGeneratingBlurb

  const baseTitle = publication ? 'Publish a new version' : 'Publish story'
  const baseSubtitle = publication
    ? `This creates version ${publication.latestVersion + 1} and keeps every previous version available.`
    : 'Create a read-only version of your manuscript.'

  return (
    <>
      <DialogHeader
        title={baseTitle}
        subtitle={baseSubtitle}
        className="publish-story-header"
        onOpenChange={() => onOpenChange(false)}
      />
      <section className="publish-story-layout">
        <Stepper
          activeStep={pageIndex}
          label="Publish steps"
          density={isMobile ? 'compact' : 'spacious'}
          orientation={isMobile ? 'horizontal' : 'vertical'}
          indicatorPosition="on-track"
          horizontalOptions={{ minimumStepWidth: 112, collapsedVariant: 'withLabel' }}
          onStepClick={goToStep}
          className="publish-story-stepper"
        >
          {pages.map((name, index) => (
            <Step
              key={name}
              step={index}
              label={PAGE_LABELS[name]}
              // Pages past the current one open only once the details gate is met, the same
              // rule Next follows; earlier pages are always a click away.
              isDisabled={isNavLocked || (index > pageIndex && !detailsComplete)}
            />
          ))}
        </Stepper>
        <section className="publish-story-body">
          {page === 'details' ? (
            <section className="publish-story-form">
              <VStack gap={1}>
                <Text type="supporting" display="block">
                  Story title
                </Text>
                <Text type="body" weight="medium" display="block">
                  {displayTitle}
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text type="supporting" display="block">
                  Genres
                </Text>
                {genreItems.length > 0 ? (
                  <HStack gap={1} wrap="wrap">
                    {genreItems.map((item) => (
                      <Token
                        key={item.id}
                        label={item.label}
                        size="sm"
                        color={item.auxiliaryData?.color}
                      />
                    ))}
                  </HStack>
                ) : (
                  <Text type="supporting" color="secondary" display="block">
                    No genres set — add at least one in Story Details.
                  </Text>
                )}
              </VStack>
              <StoryBlurbField
                description={`Required. Shown before the story; at least ${MIN_STORY_BLURB_LENGTH} characters.`}
                value={blurb}
                onChange={onBlurbChange}
                placeholder={
                  isGeneratingBlurb
                    ? 'Generating a spoiler-free blurb…'
                    : 'Describe the premise without revealing what happens…'
                }
                rows={6}
                isDisabled={isGeneratingBlurb}
              />
              <HStack hAlign="end">
                <PillButton
                  variant="secondary"
                  size="sm"
                  label={blurb ? 'Regenerate blurb' : 'Generate blurb'}
                  icon={<WandSparkles size={16} />}
                  onClick={onGenerateBlurb}
                  isLoading={isGeneratingBlurb}
                  isDisabled={isPublishing}
                />
              </HStack>
            </section>
          ) : page === 'audience' ? (
            <section className="publish-story-form">
              <RadioList
                label="Who can read this"
                value={visibility}
                onChange={(value) => onVisibilityChange(value as PublicationVisibility)}
                isDisabled={isPublishing}
                size="sm"
              >
                <RadioListItem
                  value="public"
                  label="Everyone"
                  description="Listed on Home and on your author profile."
                />
                <RadioListItem
                  value="unlisted"
                  label="Anyone with the link"
                  description="Not listed anywhere. Share the link yourself."
                />
                <RadioListItem
                  value="restricted"
                  label="Only people I choose"
                  description="Readers you list below, signed in with that email."
                />
              </RadioList>
              {visibility === 'restricted' && (
                <PublishReadersField
                  readers={readers}
                  onChange={onReadersChange}
                  isDisabled={isPublishing}
                />
              )}
            </section>
          ) : (
            <VStack gap={4} align="center">
              {coverPhase !== 'cover' && (
                <Card variant="muted" padding={3} width="100%">
                  <HStack gap={3} align="center" justify="between" wrap="wrap">
                    <VStack gap={0.5}>
                      <Text type="label" display="block">
                        Cover style
                      </Text>
                      <Text type="body" weight="medium" display="block">
                        {coverPhase === 'style'
                          ? 'Pick a style below, or write the prompt yourself'
                          : (selectedStyleLabel ?? 'Your own prompt')}
                      </Text>
                    </VStack>
                    {coverPhase === 'style' ? (
                      <PillButton
                        variant="secondary"
                        size="sm"
                        label="Write my own prompt"
                        onClick={() => setIsWritingPrompt(true)}
                        isDisabled={isPublishing}
                      />
                    ) : (
                      <PillButton
                        variant="secondary"
                        size="sm"
                        label="Change style"
                        onClick={() => {
                          onClearCoverError?.()
                          setIsWritingPrompt(false)
                          onCoverNoteChange('')
                        }}
                        isDisabled={isSuggestingPrompt || isPublishing}
                      />
                    )}
                  </HStack>
                </Card>
              )}
              {coverPhase === 'style' && (
                <>
                  {coverStylesError && (
                    <Banner
                      status="warning"
                      title="Styles unavailable"
                      description={coverStylesError}
                    />
                  )}
                  {coverError && (
                    <Banner status="error" title="Cover error" description={coverError} />
                  )}
                  <CoverStyleGallery
                    styles={sortedCoverStyles}
                    suitedKeys={suitedKeys}
                    selectedKey={selectedStyle}
                    onSelect={(key) => {
                      onSelectStyle(key)
                      if (key) onSuggestPrompt(key)
                    }}
                    isDisabled={isPublishing}
                  />
                </>
              )}
              {coverPhase === 'prompt' && isSuggestingPrompt && (
                // Sized to the cover preview so the dialog holds its height across the phases
                // instead of collapsing around the spinner.
                <VStack
                  align="center"
                  vAlign="center"
                  width="100%"
                  minHeight="calc(var(--spacing-12) * 5)"
                  padding={6}
                >
                  <Spinner size="lg" label="Drafting a prompt from your story…" />
                </VStack>
              )}
              {coverPhase === 'prompt' && !isSuggestingPrompt && (
                <>
                  <TextArea
                    width="100%"
                    label="Describe the cover you want"
                    description="Edit freely. Mood, colours, what should be on it. Up to 10,000 characters."
                    value={coverNote}
                    onChange={(value) => {
                      setIsWritingPrompt(true)
                      onCoverNoteChange(value)
                    }}
                    rows={8}
                    maxLength={10_000}
                  />
                  {coverError && (
                    <Banner status="error" title="Cover error" description={coverError} />
                  )}
                  <HStack gap={2}>
                    <PillButton
                      variant="primary"
                      size="sm"
                      label="Generate cover"
                      icon={<WandSparkles size={16} />}
                      onClick={onGenerateCover}
                      isLoading={isGeneratingCover}
                      isDisabled={isPublishing || !canGenerateCover}
                    />
                  </HStack>
                </>
              )}
              {coverPhase === 'cover' && (
                <>
                  {coverError && (
                    <Banner status="error" title="Cover error" description={coverError} />
                  )}
                  <BookCover
                    size="lg"
                    className="publish-story-cover-preview"
                    coverUrl={coverUrl}
                    title={displayTitle}
                    authorName={authorName}
                    isLoading={isGeneratingCover}
                  />
                  <Text type="supporting" display="block">
                    The title and author name are drawn into the image. If the lettering comes out
                    wrong, regenerate.
                  </Text>
                  <HStack gap={2}>
                    <PillButton
                      variant="secondary"
                      size="sm"
                      label="Regenerate"
                      icon={<WandSparkles size={16} />}
                      onClick={onGenerateCover}
                      isLoading={isGeneratingCover}
                      isDisabled={isPublishing || !canGenerateCover}
                    />
                    <PillButton
                      variant="secondary"
                      size="sm"
                      label="Remove"
                      onClick={onRemoveCover}
                      isDisabled={isGeneratingCover || isPublishing}
                    />
                  </HStack>
                </>
              )}
            </VStack>
          )}
        </section>
      </section>
      <footer className="publish-story-footer">
        <HStack gap={2} hAlign="end">
          {pageIndex === 0 ? (
            <PillButton
              variant="secondary"
              label="Cancel"
              onClick={() => onOpenChange(false)}
              isDisabled={isPublishing}
            />
          ) : (
            <PillButton
              variant="secondary"
              label="Back"
              onClick={goBack}
              isDisabled={isNavLocked}
            />
          )}
          {isLastPage ? (
            <PillButton
              variant="primary"
              label="Publish"
              onClick={onPublish}
              isLoading={isPublishing}
              isDisabled={isGeneratingCover || isPublishing || !detailsComplete}
            />
          ) : (
            <PillButton
              variant="primary"
              label="Next"
              onClick={goNext}
              isDisabled={!detailsComplete || isNavLocked}
            />
          )}
        </HStack>
      </footer>
    </>
  )
}
